import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { KANPO_NOTICE_TYPE_LABELS } from "../constants";
import type { KanpoNoticeItem } from "../types";

interface KanpoTabProps {
  notices: KanpoNoticeItem[];
  isLoading?: boolean;
  hasError?: boolean;
  onRetry?: () => void;
  /** PDFのボタンを押したときの処理。署名付きURLは押下時に発行する */
  onOpenPdf?: (item: KanpoNoticeItem) => void;
}

/** 公告の1行。行を開くと、抽出した項目と本文テキストを表示する。 */
const KanpoNoticeRow: React.FC<{
  item: KanpoNoticeItem;
  onOpenPdf?: (item: KanpoNoticeItem) => void;
}> = ({ item, onOpenPdf }) => {
  const [open, setOpen] = useState(false);
  // BE に種別が増えても表示が崩れないよう、未知の種別は代わりの名前を出す
  const typeLabel =
    (KANPO_NOTICE_TYPE_LABELS as Record<string, string>)[item.noticeType] ??
    "その他の公告";
  // 決算公告の本文は、貸借対照表の表が PDF のテキストの並びのまま崩れて読めないので出さない（項目と PDF で読む）
  const bodyText = item.noticeType === "settlement" ? null : item.bodyText;
  const hasContent = item.extractedFields.length > 0 || Boolean(bodyText);
  return (
    <>
      <TableRow sx={{ "& > td": { borderBottom: open ? "unset" : undefined } }}>
        <TableCell padding="checkbox">
          {hasContent ? (
            <IconButton
              size="small"
              aria-label={`${typeLabel}（${item.noticeDate}）の内容を${
                open ? "閉じる" : "開く"
              }`}
              aria-expanded={open}
              onClick={() => {
                setOpen((value) => !value);
              }}
            >
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          ) : null}
        </TableCell>
        <TableCell sx={{ whiteSpace: "nowrap" }}>{item.noticeDate}</TableCell>
        <TableCell>{typeLabel}</TableCell>
        <TableCell sx={{ whiteSpace: "nowrap" }}>{item.issueNumber}</TableCell>
        <TableCell align="right">{item.pageNumber}</TableCell>
        <TableCell align="right">
          {item.hasPdf && onOpenPdf ? (
            <Button
              size="small"
              startIcon={<OpenInNewIcon fontSize="small" />}
              aria-label={`${typeLabel}（${item.noticeDate}）の官報PDFを開く`}
              onClick={() => {
                onOpenPdf(item);
              }}
            >
              PDF
            </Button>
          ) : (
            "—"
          )}
        </TableCell>
      </TableRow>
      {hasContent ? (
        <TableRow>
          <TableCell colSpan={6} sx={{ py: 0 }}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Stack spacing={1.5} sx={{ py: 2 }}>
                {item.extractedFields.length > 0 ? (
                  <Stack
                    component="dl"
                    spacing={0.5}
                    sx={{ m: 0 }}
                    aria-label="抽出した項目"
                  >
                    {item.extractedFields.map((field, index) => (
                      <Stack
                        key={`${field.label}-${index}`}
                        direction="row"
                        spacing={2}
                      >
                        <Typography
                          component="dt"
                          variant="body2"
                          color="text.secondary"
                          sx={{ width: 140, flexShrink: 0 }}
                        >
                          {field.label}
                        </Typography>
                        <Typography
                          component="dd"
                          variant="body2"
                          sx={{ m: 0, overflowWrap: "anywhere" }}
                        >
                          {field.value}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                ) : null}
                {bodyText ? (
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      component="div"
                    >
                      公告の本文
                    </Typography>
                    <Typography
                      variant="body2"
                      component="p"
                      sx={{
                        m: 0,
                        whiteSpace: "pre-wrap",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {bodyText}
                    </Typography>
                  </Box>
                ) : null}
              </Stack>
            </Collapse>
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
};

/**
 * 法人マスタの詳細にある「官報」タブ。
 * 先頭に、紐づけの注意書きを出し、その下に、法人に紐づいた公告を、BE が返した順（公告日の新しい順）に並べる。
 * 行を開くと、公告から抽出した項目と本文テキストを表示する。
 */
export const KanpoTab: React.FC<KanpoTabProps> = ({
  notices,
  isLoading = false,
  hasError = false,
  onRetry,
  onOpenPdf,
}) => (
  <Stack spacing={2}>
    <Alert severity="info" icon={false}>
      <Typography variant="body2">
        官報の公告は、会社名と所在地をもとに、法人へ自動で紐づけています。
      </Typography>
      <Typography variant="body2">
        （表記の違いなどで紐づけられなかった公告は一部紐づいておりません）
      </Typography>
    </Alert>
    {isLoading ? (
      <CircularProgress aria-label="官報の公告を読み込み中" />
    ) : hasError ? (
      <Alert
        severity="error"
        action={
          onRetry ? (
            <Button color="inherit" onClick={onRetry}>
              再試行
            </Button>
          ) : undefined
        }
      >
        官報の公告の取得に失敗しました。
      </Alert>
    ) : notices.length === 0 ? (
      <Alert severity="info">この法人に紐づく官報の公告はありません</Alert>
    ) : (
      <>
        <Typography color="text.secondary">{notices.length}件</Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" aria-label="官報の公告の一覧">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>公告日</TableCell>
                <TableCell>種別</TableCell>
                <TableCell>号</TableCell>
                <TableCell align="right">ページ</TableCell>
                <TableCell align="right">官報のPDF</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {notices.map((item) => (
                <KanpoNoticeRow
                  key={item.id}
                  item={item}
                  onOpenPdf={onOpenPdf}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    )}
  </Stack>
);
